import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { ShoppingCart, User, AlertCircle, Edit, UserPlus, Users, Plus, X, Calculator, Eye, Truck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Sales() {
  const [sales, setSales] = useState([]);
  const [stock, setStock] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [showAddCustomerDialog, setShowAddCustomerDialog] = useState(false);
  const [showSaleDetailsDialog, setShowSaleDetailsDialog] = useState(false);
  const [viewingSale, setViewingSale] = useState(null);
  const [correctingLoading, setCorrectingLoading] = useState(false);
  const [correctedLoadingCharges, setCorrectedLoadingCharges] = useState(0);
  const [otherMaterials, setOtherMaterials] = useState([]);
  const [editOtherMaterials, setEditOtherMaterials] = useState([]);
  const phoneInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    customer_name: '',
    customer_phone: '',
    customer_address: '',
    fly_ash: 0,
    fly_ash_rate: 0,
    interlock_6: 0,
    interlock_6_rate: 0,
    interlock_8: 0,
    interlock_8_rate: 0,
    brick_total: 0,
    other_total: 0,
    total_amount: 0,
    amount_paid: 0,
    payment_status: 'paid',
    payment_method: 'cash',
    loading_worker_id: '',
    loading_fly_ash_rate: 0.50,
    loading_interlock_6_rate: 2.00,
    loading_interlock_8_rate: 3.00,
    loading_custom_rates: []
  });

  const [editFormData, setEditFormData] = useState({});
  
  const [newCustomerData, setNewCustomerData] = useState({
    name: '',
    phone: '',
    address: '',
    balance_due: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Auto-calculate totals when quantities or rates change
  useEffect(() => {
    const flyAshTotal = formData.fly_ash * formData.fly_ash_rate;
    const interlock6Total = formData.interlock_6 * formData.interlock_6_rate;
    const interlock8Total = formData.interlock_8 * formData.interlock_8_rate;
    const brickTotal = flyAshTotal + interlock6Total + interlock8Total;
    
    const otherTotal = otherMaterials.reduce((sum, m) => sum + (parseFloat(m.amount) || 0), 0);
    const grandTotal = brickTotal + otherTotal;
    
    setFormData(prev => ({
      ...prev,
      brick_total: brickTotal,
      other_total: otherTotal,
      total_amount: grandTotal
    }));
  }, [formData.fly_ash, formData.fly_ash_rate, formData.interlock_6, formData.interlock_6_rate, 
      formData.interlock_8, formData.interlock_8_rate, otherMaterials]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [salesRes, stockRes, workersRes, customersRes, settingsRes] = await Promise.all([
        axios.get(`${API}/sales`, { headers }),
        axios.get(`${API}/stock`, { headers }),
        axios.get(`${API}/workers`, { headers }),
        axios.get(`${API}/customers`, { headers }),
        axios.get(`${API}/settings`, { headers })
      ]);
      setSales(salesRes.data);
      setStock(stockRes.data);
      setWorkers(workersRes.data);
      setCustomers(customersRes.data);
      setSettings(settingsRes.data);
      
      // Set default rates from settings
      if (settingsRes.data) {
        const customMaterials = settingsRes.data.custom_materials || [];
        setFormData(prev => ({
          ...prev,
          fly_ash_rate: settingsRes.data.fly_ash_rate || 0,
          interlock_6_rate: settingsRes.data.interlock_6_rate || 0,
          interlock_8_rate: settingsRes.data.interlock_8_rate || 0,
          loading_fly_ash_rate: settingsRes.data.loading_fly_ash_rate || 0.50,
          loading_interlock_6_rate: settingsRes.data.loading_interlock_6_rate || 2.00,
          loading_interlock_8_rate: settingsRes.data.loading_interlock_8_rate || 3.00,
          loading_custom_rates: customMaterials.map(m => ({
            name: m.name,
            qty: 0,
            rate: m.loading_rate || 0
          }))
        }));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handlePhoneChange = async (e) => {
    const phone = e.target.value;
    setFormData({ ...formData, customer_phone: phone });
    setSelectedCustomer(null);
    
    if (phone.length >= 3) {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/customers/search?phone=${phone}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCustomerSuggestions(res.data);
        setShowSuggestions(res.data.length > 0);
      } catch (error) {
        console.error('Error searching customers:', error);
      }
    } else {
      setCustomerSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const selectCustomer = (customer) => {
    setFormData({
      ...formData,
      customer_name: customer.name,
      customer_phone: customer.phone,
      customer_address: customer.address || ''
    });
    setSelectedCustomer(customer);
    setShowSuggestions(false);
  };

  const addOtherMaterial = () => {
    setOtherMaterials([...otherMaterials, { name: '', quantity: '', rate: 0, amount: 0 }]);
  };

  const updateOtherMaterial = (index, field, value) => {
    const updated = [...otherMaterials];
    updated[index][field] = value;
    
    // Auto-calculate amount if rate is a number
    if (field === 'rate') {
      const rate = parseFloat(value) || 0;
      updated[index].amount = rate;
    }
    
    setOtherMaterials(updated);
  };

  const removeOtherMaterial = (index) => {
    setOtherMaterials(otherMaterials.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (stock && (formData.fly_ash > stock.fly_ash || formData.interlock_6 > stock.interlock_6 || formData.interlock_8 > stock.interlock_8)) {
      toast.error('Insufficient stock available');
      return;
    }

    if (formData.total_amount <= 0) {
      toast.error('Please enter items or total amount');
      return;
    }

    const payment_status = formData.amount_paid >= formData.total_amount ? 'paid' : 
                          formData.amount_paid > 0 ? 'partial' : 'pending';
    
    try {
      const token = localStorage.getItem('token');
      const submitData = {
        ...formData,
        other_materials: otherMaterials.filter(m => m.name),
        loading_custom_rates: formData.loading_custom_rates.filter(r => r.qty > 0),
        payment_status
      };
      
      await axios.post(`${API}/sales`, submitData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Sale recorded successfully');
      
      // Reset form
      const customMaterials = settings?.custom_materials || [];
      setFormData({
        date: new Date().toISOString().split('T')[0],
        customer_name: '',
        customer_phone: '',
        customer_address: '',
        fly_ash: 0,
        fly_ash_rate: settings?.fly_ash_rate || 0,
        interlock_6: 0,
        interlock_6_rate: settings?.interlock_6_rate || 0,
        interlock_8: 0,
        interlock_8_rate: settings?.interlock_8_rate || 0,
        brick_total: 0,
        other_total: 0,
        total_amount: 0,
        amount_paid: 0,
        payment_status: 'paid',
        payment_method: 'cash',
        loading_worker_id: '',
        loading_fly_ash_rate: settings?.loading_fly_ash_rate || 0.50,
        loading_interlock_6_rate: settings?.loading_interlock_6_rate || 2.00,
        loading_interlock_8_rate: settings?.loading_interlock_8_rate || 3.00,
        loading_custom_rates: customMaterials.map(m => ({
          name: m.name,
          qty: 0,
          rate: m.loading_rate || 0
        }))
      });
      setOtherMaterials([]);
      setSelectedCustomer(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record sale');
    }
  };

  const openEditDialog = (sale) => {
    setEditingSale(sale);
    setEditFormData({
      date: sale.date,
      customer_name: sale.customer_name,
      customer_phone: sale.customer_phone,
      customer_address: sale.customer_address || '',
      fly_ash: sale.fly_ash,
      fly_ash_rate: sale.fly_ash_rate || 0,
      interlock_6: sale.interlock_6,
      interlock_6_rate: sale.interlock_6_rate || 0,
      interlock_8: sale.interlock_8,
      interlock_8_rate: sale.interlock_8_rate || 0,
      brick_total: sale.brick_total || 0,
      other_total: sale.other_total || 0,
      total_amount: sale.total_amount,
      amount_paid: sale.amount_paid,
      payment_method: sale.payment_method
    });
    setEditOtherMaterials(sale.other_materials || []);
    setShowEditDialog(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingSale) return;

    try {
      const token = localStorage.getItem('token');
      const submitData = {
        ...editFormData,
        other_materials: editOtherMaterials.filter(m => m.name)
      };
      
      await axios.put(`${API}/sales/${editingSale.id}`, submitData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Sale updated successfully');
      setShowEditDialog(false);
      setEditingSale(null);
      setEditOtherMaterials([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update sale');
    }
  };

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    if (!newCustomerData.name || !newCustomerData.phone) {
      toast.error('Please enter customer name and phone');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/customers`, newCustomerData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Customer "${newCustomerData.name}" added successfully`);
      setShowAddCustomerDialog(false);
      setNewCustomerData({ name: '', phone: '', address: '', balance_due: 0 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add customer');
    }
  };

  const handleCorrectLoadingCharges = async () => {
    if (!viewingSale) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/sales/${viewingSale.id}`, {
        loading_charges: correctedLoadingCharges
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Loading charges corrected successfully');
      setCorrectingLoading(false);
      fetchData();
      // Update the viewing sale with new value
      setViewingSale(prev => ({ ...prev, loading_charges: correctedLoadingCharges }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to correct loading charges');
    }
  };

  // Calculate edit form totals
  const editBrickTotal = (editFormData.fly_ash || 0) * (editFormData.fly_ash_rate || 0) +
                         (editFormData.interlock_6 || 0) * (editFormData.interlock_6_rate || 0) +
                         (editFormData.interlock_8 || 0) * (editFormData.interlock_8_rate || 0);
  const editOtherTotal = editOtherMaterials.reduce((sum, m) => sum + (parseFloat(m.amount) || 0), 0);

  return (
    <div data-testid="sales-page" className="space-y-8">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="font-manrope font-bold text-4xl text-concrete-900 tracking-tight" data-testid="sales-title">
            <ShoppingCart className="inline-block mr-3 h-10 w-10 text-brick-600" />
            Sales
          </h1>
          <p className="font-inter text-concrete-600 mt-2">Record new sales and manage customer bills</p>
        </div>
        <Button
          onClick={() => setShowAddCustomerDialog(true)}
          className="bg-green-600 hover:bg-green-700 text-white"
        >
          <UserPlus className="mr-2 h-4 w-4" />
          Add Existing Party
        </Button>
      </div>

      <Tabs defaultValue="new-sale" className="w-full">
        <TabsList className="bg-white border border-concrete-200">
          <TabsTrigger value="new-sale" className="font-manrope font-semibold">New Sale</TabsTrigger>
          <TabsTrigger value="sales-list" className="font-manrope font-semibold">Sales History</TabsTrigger>
          <TabsTrigger value="customers" className="font-manrope font-semibold">
            <Users className="mr-2 h-4 w-4" />
            Parties/Customers
          </TabsTrigger>
        </TabsList>

        <TabsContent value="new-sale" className="mt-6">
          <form onSubmit={handleSubmit} className="bg-white border border-concrete-200 shadow-sm rounded-md p-6">
            {/* Customer Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <Label className="font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Date</Label>
                <Input
                  type="date"
                  data-testid="sale-date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="mt-2 border-concrete-300 focus:border-brick-500"
                  required
                />
              </div>
              <div>
                <Label className="font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Customer Phone</Label>
                <div className="relative">
                  <Input
                    ref={phoneInputRef}
                    data-testid="customer-phone"
                    value={formData.customer_phone}
                    onChange={handlePhoneChange}
                    onFocus={() => customerSuggestions.length > 0 && setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                    className="mt-2 border-concrete-300 focus:border-brick-500"
                    placeholder="Start typing to search existing customers"
                    required
                  />
                  {showSuggestions && customerSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-concrete-200 rounded-md shadow-lg max-h-60 overflow-auto">
                      {customerSuggestions.map((customer) => (
                        <div
                          key={customer.id}
                          className="px-4 py-3 hover:bg-concrete-50 cursor-pointer border-b border-concrete-100 last:border-b-0"
                          onClick={() => selectCustomer(customer)}
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-semibold text-concrete-900">{customer.name}</p>
                              <p className="text-sm text-concrete-600">{customer.phone}</p>
                            </div>
                            {customer.balance_due > 0 && (
                              <span className="text-sm font-semibold text-red-600 bg-red-50 px-2 py-1 rounded">
                                Due: ₹{customer.balance_due.toLocaleString()}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {selectedCustomer && (
                  <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="flex items-center gap-2 text-blue-800">
                      <User className="h-4 w-4" />
                      <span className="font-semibold">Existing Customer: {selectedCustomer.name}</span>
                    </div>
                    {selectedCustomer.balance_due > 0 && (
                      <div className="mt-1 text-sm text-red-600 font-semibold flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        Balance Due: ₹{selectedCustomer.balance_due.toLocaleString()}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <Label className="font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Customer Name</Label>
                <Input
                  data-testid="customer-name"
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  className="mt-2 border-concrete-300 focus:border-brick-500"
                  required
                />
              </div>
              <div>
                <Label className="font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Customer Address</Label>
                <Input
                  data-testid="customer-address"
                  value={formData.customer_address}
                  onChange={(e) => setFormData({ ...formData, customer_address: e.target.value })}
                  className="mt-2 border-concrete-300 focus:border-brick-500"
                />
              </div>
            </div>

            {/* Brick Sales with Rate Per Piece */}
            <div className="border-t border-concrete-200 pt-6 mb-6">
              <h3 className="font-manrope font-bold text-lg mb-4 flex items-center gap-2">
                <Calculator className="h-5 w-5 text-brick-600" />
                Brick Sales (Rate Per Piece)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Fly Ash */}
                <div className="border border-concrete-200 rounded-lg p-4">
                  <Label className="font-semibold text-concrete-700">Fly Ash Bricks</Label>
                  <p className="text-xs text-concrete-500 mb-2">Stock: {stock?.fly_ash || 0}</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Qty</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.fly_ash}
                        onChange={(e) => setFormData({ ...formData, fly_ash: parseInt(e.target.value) || 0 })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Rate (₹)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.fly_ash_rate}
                        onChange={(e) => setFormData({ ...formData, fly_ash_rate: parseFloat(e.target.value) || 0 })}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <p className="text-right mt-2 font-semibold text-brick-600">
                    = ₹{(formData.fly_ash * formData.fly_ash_rate).toLocaleString()}
                  </p>
                </div>

                {/* 6" Interlock */}
                <div className="border border-concrete-200 rounded-lg p-4">
                  <Label className="font-semibold text-concrete-700">6" Interlock</Label>
                  <p className="text-xs text-concrete-500 mb-2">Stock: {stock?.interlock_6 || 0}</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Qty</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.interlock_6}
                        onChange={(e) => setFormData({ ...formData, interlock_6: parseInt(e.target.value) || 0 })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Rate (₹)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.interlock_6_rate}
                        onChange={(e) => setFormData({ ...formData, interlock_6_rate: parseFloat(e.target.value) || 0 })}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <p className="text-right mt-2 font-semibold text-brick-600">
                    = ₹{(formData.interlock_6 * formData.interlock_6_rate).toLocaleString()}
                  </p>
                </div>

                {/* 8" Interlock */}
                <div className="border border-concrete-200 rounded-lg p-4">
                  <Label className="font-semibold text-concrete-700">8" Interlock</Label>
                  <p className="text-xs text-concrete-500 mb-2">Stock: {stock?.interlock_8 || 0}</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Qty</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.interlock_8}
                        onChange={(e) => setFormData({ ...formData, interlock_8: parseInt(e.target.value) || 0 })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Rate (₹)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.interlock_8_rate}
                        onChange={(e) => setFormData({ ...formData, interlock_8_rate: parseFloat(e.target.value) || 0 })}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <p className="text-right mt-2 font-semibold text-brick-600">
                    = ₹{(formData.interlock_8 * formData.interlock_8_rate).toLocaleString()}
                  </p>
                </div>
              </div>
              <div className="text-right mt-3 text-lg font-bold text-concrete-800">
                Brick Total: ₹{formData.brick_total.toLocaleString()}
              </div>
            </div>

            {/* Other Materials */}
            <div className="border-t border-concrete-200 pt-6 mb-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-manrope font-bold text-lg">Other Materials / Items</h3>
                <Button type="button" variant="outline" size="sm" onClick={addOtherMaterial}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Item
                </Button>
              </div>
              {otherMaterials.length === 0 ? (
                <p className="text-sm text-concrete-500">No other materials added. Click "Add Item" to add non-brick items.</p>
              ) : (
                <div className="space-y-3">
                  {otherMaterials.map((material, index) => (
                    <div key={index} className="flex gap-2 items-center bg-concrete-50 p-3 rounded-lg">
                      <div className="flex-1">
                        <Label className="text-xs">Material Name</Label>
                        <Input
                          value={material.name}
                          onChange={(e) => updateOtherMaterial(index, 'name', e.target.value)}
                          placeholder="e.g., Sand, Gravel, Transport"
                          className="mt-1"
                        />
                      </div>
                      <div className="w-32">
                        <Label className="text-xs">Quantity</Label>
                        <Input
                          value={material.quantity}
                          onChange={(e) => updateOtherMaterial(index, 'quantity', e.target.value)}
                          placeholder="e.g., 2 loads, 50kg"
                          className="mt-1"
                        />
                      </div>
                      <div className="w-28">
                        <Label className="text-xs">Amount (₹)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={material.amount}
                          onChange={(e) => updateOtherMaterial(index, 'amount', parseFloat(e.target.value) || 0)}
                          className="mt-1"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeOtherMaterial(index)}
                        className="text-red-600 hover:text-red-700 mt-5"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <div className="text-right mt-3 text-lg font-bold text-concrete-800">
                    Other Items Total: ₹{formData.other_total.toLocaleString()}
                  </div>
                </div>
              )}
            </div>

            {/* Payment Section */}
            <div className="border-t border-concrete-200 pt-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <Label className="font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Grand Total (₹)</Label>
                  <div className="mt-2 p-3 bg-brick-50 border border-brick-200 rounded-md">
                    <p className="text-2xl font-bold text-brick-600">₹{formData.total_amount.toLocaleString()}</p>
                    <p className="text-xs text-concrete-600 mt-1">
                      Bricks: ₹{formData.brick_total.toLocaleString()} + Others: ₹{formData.other_total.toLocaleString()}
                    </p>
                  </div>
                </div>
                <div>
                  <Label className="font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Amount Paid (₹)</Label>
                  <Input
                    type="number"
                    min="0"
                    data-testid="amount-paid"
                    value={formData.amount_paid}
                    onChange={(e) => setFormData({ ...formData, amount_paid: parseFloat(e.target.value) || 0 })}
                    className="mt-2 border-concrete-300 focus:border-brick-500 tabular-nums"
                  />
                </div>
                <div>
                  <Label className="font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Payment Method</Label>
                  <Select value={formData.payment_method} onValueChange={(value) => setFormData({ ...formData, payment_method: value })}>
                    <SelectTrigger className="mt-2 border-concrete-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="upi">UPI</SelectItem>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                      <SelectItem value="cheque">Cheque</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {formData.amount_paid < formData.total_amount && formData.total_amount > 0 && (
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md">
                  <p className="text-amber-800 font-semibold">
                    Balance Due: ₹{(formData.total_amount - formData.amount_paid).toLocaleString()}
                  </p>
                </div>
              )}
            </div>

            {/* Loading Charges */}
            <div className="border-t border-concrete-200 pt-6 mb-6">
              <h3 className="font-manrope font-bold text-lg mb-4">Loading Charges (Optional)</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div>
                  <Label className="font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Loading Worker</Label>
                  <Select value={formData.loading_worker_id || "none"} onValueChange={(value) => setFormData({ ...formData, loading_worker_id: value === "none" ? "" : value })}>
                    <SelectTrigger className="mt-2 border-concrete-300">
                      <SelectValue placeholder="Select worker" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {workers.map((worker) => (
                        <SelectItem key={worker.id} value={worker.id}>{worker.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Fly Ash Rate (₹/pc)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.loading_fly_ash_rate}
                    onChange={(e) => setFormData({ ...formData, loading_fly_ash_rate: parseFloat(e.target.value) || 0 })}
                    className="mt-2 border-concrete-300 focus:border-brick-500 tabular-nums"
                  />
                </div>
                <div>
                  <Label className="font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">6" IL Rate (₹/pc)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.loading_interlock_6_rate}
                    onChange={(e) => setFormData({ ...formData, loading_interlock_6_rate: parseFloat(e.target.value) || 0 })}
                    className="mt-2 border-concrete-300 focus:border-brick-500 tabular-nums"
                  />
                </div>
                <div>
                  <Label className="font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">8" IL Rate (₹/pc)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.loading_interlock_8_rate}
                    onChange={(e) => setFormData({ ...formData, loading_interlock_8_rate: parseFloat(e.target.value) || 0 })}
                    className="mt-2 border-concrete-300 focus:border-brick-500 tabular-nums"
                  />
                </div>
              </div>
              
              {/* Custom Material Loading Rates */}
              {formData.loading_worker_id && formData.loading_custom_rates.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-manrope font-semibold text-sm text-concrete-700 mb-3">Custom Materials Loading</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {formData.loading_custom_rates.map((item, index) => (
                      <div key={index} className="bg-concrete-50 p-3 rounded-md border border-concrete-200">
                        <Label className="font-manrope font-semibold text-xs text-concrete-700">{item.name}</Label>
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          <div>
                            <span className="text-xs text-concrete-500">Qty</span>
                            <Input
                              type="number"
                              min="0"
                              value={item.qty}
                              onChange={(e) => {
                                const updated = [...formData.loading_custom_rates];
                                updated[index].qty = parseInt(e.target.value) || 0;
                                setFormData({ ...formData, loading_custom_rates: updated });
                              }}
                              className="border-concrete-300 focus:border-brick-500 tabular-nums h-8"
                            />
                          </div>
                          <div>
                            <span className="text-xs text-concrete-500">Rate (₹/pc)</span>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={item.rate}
                              onChange={(e) => {
                                const updated = [...formData.loading_custom_rates];
                                updated[index].rate = parseFloat(e.target.value) || 0;
                                setFormData({ ...formData, loading_custom_rates: updated });
                              }}
                              className="border-concrete-300 focus:border-brick-500 tabular-nums h-8"
                            />
                          </div>
                        </div>
                        {item.qty > 0 && item.rate > 0 && (
                          <p className="text-xs text-green-600 mt-1 font-semibold">
                            = ₹{(item.qty * item.rate).toLocaleString()}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Button type="submit" data-testid="submit-sale" className="bg-brick-600 hover:bg-brick-500 text-white shadow-md font-manrope font-semibold">
              <ShoppingCart className="mr-2 h-4 w-4" />
              Record Sale
            </Button>
          </form>
        </TabsContent>

        <TabsContent value="sales-list" className="mt-6">
          <div className="bg-white border border-concrete-200 shadow-sm rounded-md overflow-hidden">
            <div className="p-6 border-b border-concrete-200">
              <h2 className="font-manrope font-bold text-xl text-concrete-900">Sales History</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-concrete-50 border-b border-concrete-200">
                  <tr>
                    <th className="px-4 py-3 text-left font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Date</th>
                    <th className="px-4 py-3 text-left font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Customer</th>
                    <th className="px-4 py-3 text-right font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Bricks</th>
                    <th className="px-4 py-3 text-left font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Other Items</th>
                    <th className="px-4 py-3 text-right font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Total</th>
                    <th className="px-4 py-3 text-right font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Paid</th>
                    <th className="px-4 py-3 text-right font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Due</th>
                    <th className="px-4 py-3 text-center font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Status</th>
                    <th className="px-4 py-3 text-center font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-concrete-100">
                  {sales.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="px-6 py-12 text-center font-inter text-concrete-500">No sales recorded yet</td>
                    </tr>
                  ) : (
                    sales.map((sale) => {
                      const totalBricks = sale.fly_ash + sale.interlock_6 + sale.interlock_8;
                      return (
                        <tr key={sale.id} className="hover:bg-concrete-50">
                          <td className="px-4 py-4 font-inter text-concrete-700">{sale.date}</td>
                          <td className="px-4 py-4">
                            <p className="font-inter font-medium text-concrete-900">{sale.customer_name}</p>
                            <p className="font-inter text-sm text-concrete-500">{sale.customer_phone}</p>
                          </td>
                          <td className="px-4 py-4 font-inter text-concrete-900 text-right tabular-nums">{totalBricks}</td>
                          <td className="px-4 py-4 font-inter text-concrete-600 text-sm">
                            {sale.other_materials && sale.other_materials.length > 0 ? (
                              <div className="space-y-1">
                                {sale.other_materials.slice(0, 2).map((om, idx) => (
                                  <div key={idx} className="text-xs bg-concrete-100 px-2 py-1 rounded">
                                    {om.name}: {om.quantity}
                                  </div>
                                ))}
                                {sale.other_materials.length > 2 && (
                                  <span className="text-xs text-concrete-500">+{sale.other_materials.length - 2} more</span>
                                )}
                              </div>
                            ) : '-'}
                          </td>
                          <td className="px-4 py-4 font-inter font-semibold text-concrete-900 text-right tabular-nums">₹{sale.total_amount?.toLocaleString()}</td>
                          <td className="px-4 py-4 font-inter text-green-600 text-right tabular-nums">₹{sale.amount_paid?.toLocaleString()}</td>
                          <td className="px-4 py-4 font-inter text-red-600 text-right tabular-nums">₹{sale.amount_due?.toLocaleString() || 0}</td>
                          <td className="px-4 py-4 text-center">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              sale.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                              sale.payment_status === 'partial' ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {sale.payment_status}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                onClick={() => {
                                  setViewingSale(sale);
                                  setShowSaleDetailsDialog(true);
                                }}
                                variant="ghost"
                                size="sm"
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                title="View Details"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                onClick={() => openEditDialog(sale)}
                                variant="ghost"
                                size="sm"
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                title="Edit Sale"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="customers" className="mt-6">
          <div className="bg-white border border-concrete-200 shadow-sm rounded-md overflow-hidden">
            <div className="p-6 border-b border-concrete-200 flex justify-between items-center">
              <div>
                <h2 className="font-manrope font-bold text-xl text-concrete-900">Parties / Customers</h2>
                <p className="text-sm text-concrete-600 mt-1">All registered customers with their balance</p>
              </div>
              <Button
                onClick={() => setShowAddCustomerDialog(true)}
                className="bg-green-600 hover:bg-green-700"
              >
                <UserPlus className="mr-2 h-4 w-4" />
                Add Party
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-concrete-50 border-b border-concrete-200">
                  <tr>
                    <th className="px-6 py-3 text-left font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Name</th>
                    <th className="px-6 py-3 text-left font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Phone</th>
                    <th className="px-6 py-3 text-left font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Address</th>
                    <th className="px-6 py-3 text-right font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Total Sales</th>
                    <th className="px-6 py-3 text-right font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Paid</th>
                    <th className="px-6 py-3 text-right font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Balance Due</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-concrete-100">
                  {customers.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center font-inter text-concrete-500">No customers registered yet</td>
                    </tr>
                  ) : (
                    customers.map((customer) => (
                      <tr key={customer.id} className="hover:bg-concrete-50">
                        <td className="px-6 py-4 font-inter font-medium text-concrete-900">{customer.name}</td>
                        <td className="px-6 py-4 font-inter text-concrete-700">{customer.phone}</td>
                        <td className="px-6 py-4 font-inter text-concrete-600">{customer.address || '-'}</td>
                        <td className="px-6 py-4 font-inter font-semibold text-concrete-900 text-right tabular-nums">₹{customer.total_sales?.toLocaleString() || 0}</td>
                        <td className="px-6 py-4 font-inter text-green-600 text-right tabular-nums">₹{customer.total_paid?.toLocaleString() || 0}</td>
                        <td className="px-6 py-4 text-right">
                          {customer.balance_due > 0 ? (
                            <span className="font-inter font-bold text-red-600 tabular-nums">₹{customer.balance_due.toLocaleString()}</span>
                          ) : (
                            <span className="font-inter text-green-600 tabular-nums">₹0</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit Sale Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-manrope font-bold flex items-center gap-2">
              <Edit className="h-5 w-5 text-brick-600" />
              Edit Sale
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={editFormData.date || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, date: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Customer Phone</Label>
                <Input
                  value={editFormData.customer_phone || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, customer_phone: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Customer Name</Label>
                <Input
                  value={editFormData.customer_name || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, customer_name: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Customer Address</Label>
                <Input
                  value={editFormData.customer_address || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, customer_address: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            
            {/* Brick Sales Edit */}
            <div className="border-t pt-4">
              <h4 className="font-semibold mb-3">Brick Sales</h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="border rounded-lg p-3">
                  <Label className="text-sm">Fly Ash</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <Input
                      type="number"
                      placeholder="Qty"
                      value={editFormData.fly_ash || 0}
                      onChange={(e) => setEditFormData({ ...editFormData, fly_ash: parseInt(e.target.value) || 0 })}
                    />
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Rate"
                      value={editFormData.fly_ash_rate || 0}
                      onChange={(e) => setEditFormData({ ...editFormData, fly_ash_rate: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                </div>
                <div className="border rounded-lg p-3">
                  <Label className="text-sm">6" Interlock</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <Input
                      type="number"
                      placeholder="Qty"
                      value={editFormData.interlock_6 || 0}
                      onChange={(e) => setEditFormData({ ...editFormData, interlock_6: parseInt(e.target.value) || 0 })}
                    />
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Rate"
                      value={editFormData.interlock_6_rate || 0}
                      onChange={(e) => setEditFormData({ ...editFormData, interlock_6_rate: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                </div>
                <div className="border rounded-lg p-3">
                  <Label className="text-sm">8" Interlock</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <Input
                      type="number"
                      placeholder="Qty"
                      value={editFormData.interlock_8 || 0}
                      onChange={(e) => setEditFormData({ ...editFormData, interlock_8: parseInt(e.target.value) || 0 })}
                    />
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Rate"
                      value={editFormData.interlock_8_rate || 0}
                      onChange={(e) => setEditFormData({ ...editFormData, interlock_8_rate: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                </div>
              </div>
              <p className="text-right mt-2 font-semibold">Brick Total: ₹{editBrickTotal.toLocaleString()}</p>
            </div>

            {/* Other Materials Edit */}
            <div className="border-t pt-4">
              <div className="flex justify-between items-center mb-3">
                <h4 className="font-semibold">Other Materials</h4>
                <Button type="button" variant="outline" size="sm" onClick={() => setEditOtherMaterials([...editOtherMaterials, { name: '', quantity: '', amount: 0 }])}>
                  <Plus className="h-4 w-4 mr-1" /> Add
                </Button>
              </div>
              {editOtherMaterials.length === 0 ? (
                <p className="text-sm text-concrete-500">No other materials</p>
              ) : (
                <div className="space-y-2">
                  {editOtherMaterials.map((material, index) => (
                    <div key={index} className="flex gap-2 items-center">
                      <Input
                        value={material.name}
                        onChange={(e) => {
                          const updated = [...editOtherMaterials];
                          updated[index].name = e.target.value;
                          setEditOtherMaterials(updated);
                        }}
                        placeholder="Material name"
                        className="flex-1"
                      />
                      <Input
                        value={material.quantity}
                        onChange={(e) => {
                          const updated = [...editOtherMaterials];
                          updated[index].quantity = e.target.value;
                          setEditOtherMaterials(updated);
                        }}
                        placeholder="Quantity"
                        className="w-28"
                      />
                      <Input
                        type="number"
                        value={material.amount}
                        onChange={(e) => {
                          const updated = [...editOtherMaterials];
                          updated[index].amount = parseFloat(e.target.value) || 0;
                          setEditOtherMaterials(updated);
                        }}
                        placeholder="Amount"
                        className="w-28"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditOtherMaterials(editOtherMaterials.filter((_, i) => i !== index))}
                        className="text-red-600"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <p className="text-right font-semibold">Other Total: ₹{editOtherTotal.toLocaleString()}</p>
                </div>
              )}
            </div>

            {/* Payment Edit */}
            <div className="border-t pt-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Total Amount (₹)</Label>
                  <div className="mt-1 p-2 bg-brick-50 rounded font-bold text-brick-600">
                    ₹{(editBrickTotal + editOtherTotal).toLocaleString()}
                  </div>
                </div>
                <div>
                  <Label>Amount Paid (₹)</Label>
                  <Input
                    type="number"
                    value={editFormData.amount_paid || 0}
                    onChange={(e) => setEditFormData({ ...editFormData, amount_paid: parseFloat(e.target.value) || 0 })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Payment Method</Label>
                  <Select value={editFormData.payment_method} onValueChange={(value) => setEditFormData({ ...editFormData, payment_method: value })}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="upi">UPI</SelectItem>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                      <SelectItem value="cheque">Cheque</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="submit" className="flex-1 bg-brick-600 hover:bg-brick-500">
                Save Changes
              </Button>
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Existing Customer Dialog */}
      <Dialog open={showAddCustomerDialog} onOpenChange={setShowAddCustomerDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-manrope font-bold flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-green-600" />
              Add Existing Party / Customer
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-concrete-600">Add a customer with their existing outstanding balance</p>
          <form onSubmit={handleAddCustomer} className="space-y-4">
            <div>
              <Label>Customer Name *</Label>
              <Input
                value={newCustomerData.name}
                onChange={(e) => setNewCustomerData({ ...newCustomerData, name: e.target.value })}
                className="mt-1"
                placeholder="Enter customer name"
                required
              />
            </div>
            <div>
              <Label>Phone Number *</Label>
              <Input
                value={newCustomerData.phone}
                onChange={(e) => setNewCustomerData({ ...newCustomerData, phone: e.target.value })}
                className="mt-1"
                placeholder="Enter phone number"
                required
              />
            </div>
            <div>
              <Label>Address</Label>
              <Input
                value={newCustomerData.address}
                onChange={(e) => setNewCustomerData({ ...newCustomerData, address: e.target.value })}
                className="mt-1"
                placeholder="Enter address (optional)"
              />
            </div>
            <div>
              <Label>Existing Balance Due (₹)</Label>
              <Input
                type="number"
                min="0"
                value={newCustomerData.balance_due}
                onChange={(e) => setNewCustomerData({ ...newCustomerData, balance_due: parseFloat(e.target.value) || 0 })}
                className="mt-1"
                placeholder="Enter outstanding balance"
              />
              <p className="text-xs text-concrete-500 mt-1">Enter the amount the customer already owes</p>
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700">
                <UserPlus className="mr-2 h-4 w-4" />
                Add Customer
              </Button>
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowAddCustomerDialog(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Sale Details Dialog */}
      <Dialog open={showSaleDetailsDialog} onOpenChange={setShowSaleDetailsDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-manrope font-bold flex items-center gap-2 text-lg">
              <ShoppingCart className="h-5 w-5 text-brick-600" />
              Sale Details
            </DialogTitle>
          </DialogHeader>
          
          {viewingSale && (
            <div className="space-y-6">
              {/* Customer Info */}
              <div className="bg-concrete-50 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-concrete-500">Date</p>
                    <p className="font-semibold">{viewingSale.date}</p>
                  </div>
                  <div>
                    <p className="text-xs text-concrete-500">Customer</p>
                    <p className="font-semibold">{viewingSale.customer_name}</p>
                    <p className="text-sm text-concrete-600">{viewingSale.customer_phone}</p>
                  </div>
                </div>
              </div>

              {/* Brick Sales Details */}
              <div>
                <h3 className="font-semibold text-concrete-800 mb-3 flex items-center gap-2">
                  <span className="bg-brick-100 text-brick-700 px-2 py-1 rounded text-xs">BRICKS</span>
                  Sales Breakdown
                </h3>
                <table className="w-full text-sm border border-concrete-200 rounded-lg overflow-hidden">
                  <thead className="bg-brick-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-concrete-700">Item</th>
                      <th className="px-3 py-2 text-center font-semibold text-concrete-700">Qty</th>
                      <th className="px-3 py-2 text-center font-semibold text-concrete-700">Rate</th>
                      <th className="px-3 py-2 text-right font-semibold text-concrete-700">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-concrete-100">
                    <tr>
                      <td className="px-3 py-2">Fly Ash Bricks</td>
                      <td className="px-3 py-2 text-center">{viewingSale.fly_ash || 0}</td>
                      <td className="px-3 py-2 text-center">₹{viewingSale.fly_ash_rate || 0}</td>
                      <td className="px-3 py-2 text-right">₹{((viewingSale.fly_ash || 0) * (viewingSale.fly_ash_rate || 0)).toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2">6" Interlock Bricks</td>
                      <td className="px-3 py-2 text-center">{viewingSale.interlock_6 || 0}</td>
                      <td className="px-3 py-2 text-center">₹{viewingSale.interlock_6_rate || 0}</td>
                      <td className="px-3 py-2 text-right">₹{((viewingSale.interlock_6 || 0) * (viewingSale.interlock_6_rate || 0)).toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2">8" Interlock Bricks</td>
                      <td className="px-3 py-2 text-center">{viewingSale.interlock_8 || 0}</td>
                      <td className="px-3 py-2 text-center">₹{viewingSale.interlock_8_rate || 0}</td>
                      <td className="px-3 py-2 text-right">₹{((viewingSale.interlock_8 || 0) * (viewingSale.interlock_8_rate || 0)).toFixed(2)}</td>
                    </tr>
                    <tr className="bg-brick-50 font-semibold">
                      <td colSpan="3" className="px-3 py-2 text-right">Brick Total:</td>
                      <td className="px-3 py-2 text-right">₹{(viewingSale.brick_total || 0).toFixed(2)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Other Materials */}
              {viewingSale.other_materials && viewingSale.other_materials.length > 0 && (
                <div>
                  <h3 className="font-semibold text-concrete-800 mb-3 flex items-center gap-2">
                    <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs">OTHER</span>
                    Other Materials
                  </h3>
                  <table className="w-full text-sm border border-concrete-200 rounded-lg overflow-hidden">
                    <thead className="bg-purple-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold text-concrete-700">Item</th>
                        <th className="px-3 py-2 text-center font-semibold text-concrete-700">Qty</th>
                        <th className="px-3 py-2 text-center font-semibold text-concrete-700">Rate</th>
                        <th className="px-3 py-2 text-right font-semibold text-concrete-700">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-concrete-100">
                      {viewingSale.other_materials.map((om, idx) => (
                        <tr key={idx}>
                          <td className="px-3 py-2">{om.name}</td>
                          <td className="px-3 py-2 text-center">{om.quantity}</td>
                          <td className="px-3 py-2 text-center">₹{om.rate || 0}</td>
                          <td className="px-3 py-2 text-right">₹{(om.amount || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                      <tr className="bg-purple-50 font-semibold">
                        <td colSpan="3" className="px-3 py-2 text-right">Other Total:</td>
                        <td className="px-3 py-2 text-right">₹{(viewingSale.other_total || 0).toFixed(2)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {/* Loading Charges - Key Section for Debugging */}
              {viewingSale.loading_worker_id && (
                <div>
                  <h3 className="font-semibold text-concrete-800 mb-3 flex items-center gap-2">
                    <Truck className="h-4 w-4 text-blue-600" />
                    <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs">LOADING</span>
                    Loading Charges Breakdown
                  </h3>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800 mb-3">
                      <strong>Loading Worker:</strong> {workers.find(w => w.id === viewingSale.loading_worker_id)?.name || viewingSale.loading_worker_id}
                    </p>
                    <table className="w-full text-sm border border-blue-200 rounded-lg overflow-hidden bg-white">
                      <thead className="bg-blue-100">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold text-blue-800">Item</th>
                          <th className="px-3 py-2 text-center font-semibold text-blue-800">Qty Loaded</th>
                          <th className="px-3 py-2 text-center font-semibold text-blue-800">Rate/pc</th>
                          <th className="px-3 py-2 text-right font-semibold text-blue-800">Loading Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-blue-100">
                        <tr>
                          <td className="px-3 py-2">Fly Ash</td>
                          <td className="px-3 py-2 text-center">{viewingSale.fly_ash || 0}</td>
                          <td className="px-3 py-2 text-center font-mono bg-yellow-50">₹{viewingSale.loading_fly_ash_rate || 0}</td>
                          <td className="px-3 py-2 text-right">₹{((viewingSale.fly_ash || 0) * (viewingSale.loading_fly_ash_rate || 0)).toFixed(2)}</td>
                        </tr>
                        <tr>
                          <td className="px-3 py-2">6" Interlock</td>
                          <td className="px-3 py-2 text-center">{viewingSale.interlock_6 || 0}</td>
                          <td className="px-3 py-2 text-center font-mono bg-yellow-50">₹{viewingSale.loading_interlock_6_rate || 0}</td>
                          <td className="px-3 py-2 text-right">₹{((viewingSale.interlock_6 || 0) * (viewingSale.loading_interlock_6_rate || 0)).toFixed(2)}</td>
                        </tr>
                        <tr>
                          <td className="px-3 py-2">8" Interlock</td>
                          <td className="px-3 py-2 text-center">{viewingSale.interlock_8 || 0}</td>
                          <td className="px-3 py-2 text-center font-mono bg-yellow-50">₹{viewingSale.loading_interlock_8_rate || 0}</td>
                          <td className="px-3 py-2 text-right">₹{((viewingSale.interlock_8 || 0) * (viewingSale.loading_interlock_8_rate || 0)).toFixed(2)}</td>
                        </tr>
                        {/* Custom materials loading */}
                        {viewingSale.loading_custom_rates && viewingSale.loading_custom_rates.filter(cr => cr.qty > 0).map((cr, idx) => (
                          <tr key={idx}>
                            <td className="px-3 py-2">{cr.name}</td>
                            <td className="px-3 py-2 text-center">{cr.qty || 0}</td>
                            <td className="px-3 py-2 text-center font-mono bg-yellow-50">₹{cr.rate || 0}</td>
                            <td className="px-3 py-2 text-right">₹{((cr.qty || 0) * (cr.rate || 0)).toFixed(2)}</td>
                          </tr>
                        ))}
                        <tr className="bg-blue-100 font-bold">
                          <td colSpan="3" className="px-3 py-2 text-right text-blue-800">Total Loading Charges:</td>
                          <td className="px-3 py-2 text-right text-blue-800">₹{(viewingSale.loading_charges || 0).toFixed(2)}</td>
                        </tr>
                        {/* Show verification if there's a mismatch */}
                        {(() => {
                          const calculatedTotal = 
                            ((viewingSale.fly_ash || 0) * (viewingSale.loading_fly_ash_rate || 0)) +
                            ((viewingSale.interlock_6 || 0) * (viewingSale.loading_interlock_6_rate || 0)) +
                            ((viewingSale.interlock_8 || 0) * (viewingSale.loading_interlock_8_rate || 0)) +
                            ((viewingSale.loading_custom_rates || []).reduce((sum, cr) => sum + ((cr.qty || 0) * (cr.rate || 0)), 0));
                          const storedTotal = viewingSale.loading_charges || 0;
                          const diff = Math.abs(storedTotal - calculatedTotal);
                          if (diff > 0.01) {
                            return (
                              <>
                                <tr className="bg-orange-100">
                                  <td colSpan="4" className="px-3 py-2 text-center text-orange-700 text-xs">
                                    ⚠️ Stored total (₹{storedTotal.toFixed(2)}) differs from visible items (₹{calculatedTotal.toFixed(2)}) by ₹{diff.toFixed(2)}.
                                  </td>
                                </tr>
                                <tr className="bg-orange-50">
                                  <td colSpan="4" className="px-3 py-2 text-center">
                                    {!correctingLoading ? (
                                      <Button 
                                        size="sm" 
                                        variant="outline"
                                        className="text-orange-700 border-orange-400 hover:bg-orange-100"
                                        onClick={() => {
                                          setCorrectingLoading(true);
                                          setCorrectedLoadingCharges(calculatedTotal);
                                        }}
                                      >
                                        <Edit className="h-3 w-3 mr-1" />
                                        Correct to ₹{calculatedTotal.toFixed(2)}
                                      </Button>
                                    ) : (
                                      <div className="flex items-center justify-center gap-2">
                                        <Input
                                          type="number"
                                          step="0.01"
                                          value={correctedLoadingCharges}
                                          onChange={(e) => setCorrectedLoadingCharges(parseFloat(e.target.value) || 0)}
                                          className="w-28 h-8 text-sm"
                                        />
                                        <Button size="sm" onClick={handleCorrectLoadingCharges} className="bg-green-600 hover:bg-green-700 h-8">
                                          Save
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={() => setCorrectingLoading(false)} className="h-8">
                                          Cancel
                                        </Button>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              </>
                            );
                          }
                          return null;
                        })()}
                      </tbody>
                    </table>
                    <p className="text-xs text-blue-600 mt-2 italic">
                      Calculation: (Fly Ash × Rate) + (6" IL × Rate) + (8" IL × Rate) + Custom Materials
                    </p>
                  </div>
                </div>
              )}

              {/* Payment Summary */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
                <h3 className="font-semibold text-green-800 mb-3">Payment Summary</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div className="bg-white rounded-md p-3 shadow-sm">
                    <p className="text-concrete-500">Grand Total</p>
                    <p className="font-bold text-lg">₹{(viewingSale.total_amount || 0).toLocaleString()}</p>
                  </div>
                  <div className="bg-white rounded-md p-3 shadow-sm">
                    <p className="text-concrete-500">Amount Paid</p>
                    <p className="font-bold text-lg text-green-600">₹{(viewingSale.amount_paid || 0).toLocaleString()}</p>
                  </div>
                  <div className="bg-white rounded-md p-3 shadow-sm">
                    <p className="text-concrete-500">Balance Due</p>
                    <p className="font-bold text-lg text-red-600">₹{(viewingSale.amount_due || 0).toLocaleString()}</p>
                  </div>
                  <div className="bg-white rounded-md p-3 shadow-sm">
                    <p className="text-concrete-500">Status</p>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      viewingSale.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                      viewingSale.payment_status === 'partial' ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {viewingSale.payment_status}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
